import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Chevron from 'assets/icons/Chevron'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearAccommodationInfo, getCampsWithOccupancies } from 'features/accommodation/accommodationSlice'
import { RootState } from 'store'
import { IComputedCamp } from 'types/accommodation.interface'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { tokens } from 'styling/tokens'
import { BarTrack, BarFill } from 'pages/HrDashboard/hrLayout.styled'
import { BedIcon } from '../icons/BedIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { BlockedIcon } from '../icons/BlockedIcon'
import { DirtyIcon } from '../icons/DirtyIcon'
import { aggregateCounts, buildOccupantMap, computeCamps } from '../accommodationStats'
import {
  StatBand,
  SectionLabel,
  SectionCount,
  CampList,
  CampCard,
  CampHeader,
  CampIdentity,
  CampName,
  CampMeta,
  CampKpis,
  CampKpi,
  CampKpiValue,
  CampKpiLabel,
  ChevronBox,
  CampCollapse,
  CampCollapseInner,
  BlockList,
  BlockRow,
  BlockIdentity,
  BlockName,
  BlockMeta,
  BlockBar,
  BlockBarValue,
  CountGroup,
  CountChip,
  CountDot,
  CountValue,
  CountLabel,
  BlockEmpty,
  ScopeTag,
} from './AccommodationDashboard.styled'

const C = {
  accent: tokens.accent.base,
  available: tokens.status.success.fg,
  occupied: tokens.status.info.fg,
  dirty: tokens.status.warning.fg,
  blocked: tokens.status.danger.fg,
}

const Count = ({ color, value, label }: { color: string; value: number; label: string }) => {
  const dim = value === 0
  return (
    <CountChip>
      <CountDot $color={dim ? tokens.border.strong : color} />
      <CountValue $dim={dim}>{value}</CountValue>
      <CountLabel>{label}</CountLabel>
    </CountChip>
  )
}

const CampSection = ({
  camp,
  active,
  onToggle,
}: {
  camp: IComputedCamp
  active: boolean
  onToggle: () => void
}) => (
  <CampCard $active={active}>
    <CampHeader onClick={onToggle}>
      <CampIdentity>
        <CampName>{camp.name}</CampName>
        <CampMeta>
          {camp.blocksCount} blocks · {camp.counts.total} beds
        </CampMeta>
      </CampIdentity>
      <CampKpis>
        <CampKpi>
          <CampKpiValue $color={C.occupied}>{camp.counts.occupancy}%</CampKpiValue>
          <CampKpiLabel>Camp occupancy</CampKpiLabel>
        </CampKpi>
        <CampKpi>
          <CampKpiValue $color={C.available}>{camp.counts.available}</CampKpiValue>
          <CampKpiLabel>Available</CampKpiLabel>
        </CampKpi>
        <CampKpi>
          <CampKpiValue $color={camp.counts.dirty ? C.dirty : tokens.text.secondary}>
            {camp.counts.dirty}
          </CampKpiValue>
          <CampKpiLabel>Cleaning</CampKpiLabel>
        </CampKpi>
        <CampKpi>
          <CampKpiValue $color={camp.counts.blocked ? C.blocked : tokens.text.secondary}>
            {camp.counts.blocked}
          </CampKpiValue>
          <CampKpiLabel>Blocked</CampKpiLabel>
        </CampKpi>
        <ChevronBox>
          <Chevron open={active} />
        </ChevronBox>
      </CampKpis>
    </CampHeader>

    <CampCollapse $open={active}>
      <CampCollapseInner>
        <BlockList>
          {camp.blocks.length ? (
            camp.blocks.map((b, i) => (
              <BlockRow key={b.id}>
                <BlockIdentity>
                  <BlockName>{b.name || `Block ${i + 1}`}</BlockName>
                  <BlockMeta>
                    {b.rooms.length} {b.rooms.length === 1 ? 'room' : 'rooms'} · {b.counts.total}{' '}
                    {b.counts.total === 1 ? 'bed' : 'beds'}
                  </BlockMeta>
                </BlockIdentity>
                <BlockBar>
                  <BarTrack>
                    <BarFill $pct={b.counts.occupancy} $color={C.occupied} />
                  </BarTrack>
                  <BlockBarValue>{b.counts.occupancy}%</BlockBarValue>
                  <ScopeTag>block</ScopeTag>
                </BlockBar>
                <CountGroup>
                  <Count color={C.available} value={b.counts.available} label='Available' />
                  <Count color={C.occupied} value={b.counts.occupied} label='Occupied' />
                  <Count color={C.dirty} value={b.counts.dirty} label='Dirty' />
                  <Count color={C.blocked} value={b.counts.blocked} label='Blocked' />
                </CountGroup>
              </BlockRow>
            ))
          ) : (
            <BlockEmpty>No blocks configured for this camp</BlockEmpty>
          )}
        </BlockList>
      </CampCollapseInner>
    </CampCollapse>
  </CampCard>
)

export const AccomodationDashboard = () => {
  const [params] = useState({ page: 1, limit: 99 })
  const [openCampId, setOpenCampId] = useState<number | null>(null)
  const { camps, bedsHistory } = useSelector((state: RootState) => state.accomodation)

  useQueryPagination({
    fetchFunctions: [getCampsWithOccupancies],
    params,
    cleanUpFunctions: [clearAccommodationInfo],
  })

  const occupantByBed = useMemo(() => buildOccupantMap(bedsHistory?.data || []), [bedsHistory])
  const campsData = camps?.data || []
  const computed = useMemo(() => computeCamps(campsData, occupantByBed), [campsData, occupantByBed])
  const totals = useMemo(() => aggregateCounts(computed), [computed])

  return (
    <>
      <StatBand>
        <MetricCard
          layout='inline'
          label='Total capacity'
          value={String(totals.total)}
          icon={<BedIcon />}
          accentColor={C.accent}
        />
        <MetricCard
          layout='inline'
          label='Available'
          value={String(totals.available)}
          icon={<BedIcon color={C.available} />}
          accentColor={C.available}
        />
        <MetricCard
          layout='inline'
          label='Occupied'
          value={String(totals.occupied)}
          icon={<OccupiedBedIcon />}
          accentColor={C.occupied}
          trailing={<StatusPill status='info'>{totals.occupancy}%</StatusPill>}
        />
        <MetricCard
          layout='inline'
          label='Dirty'
          value={String(totals.dirty)}
          icon={<DirtyIcon />}
          accentColor={C.dirty}
        />
        <MetricCard
          layout='inline'
          label='Blocked'
          value={String(totals.blocked)}
          icon={<BlockedIcon />}
          accentColor={C.blocked}
        />
      </StatBand>

      <SectionLabel>
        Camps <SectionCount>{computed.length}</SectionCount>
      </SectionLabel>
      <CampList>
        {computed.map((c) => (
          <CampSection
            key={c.id}
            camp={c}
            active={openCampId === c.id}
            onToggle={() => setOpenCampId((prev) => (prev === c.id ? null : c.id))}
          />
        ))}
      </CampList>
    </>
  )
}
